import { convertWeight, convertDistance, convertTemperature } from '@/hooks/useMeasurementPreferences';

interface UserPreferences {
  weight_unit: 'kg' | 'lbs';
  distance_unit: 'km' | 'miles';
  temperature_unit: 'celsius' | 'fahrenheit';
  time_format: 'hh:mm:ss' | 'mm:ss' | 'decimal_minutes';
  pace_format: 'min_per_km' | 'min_per_mile' | 'km_per_hour' | 'miles_per_hour';
}

// Format weight with user preferences
export const formatWeightDisplay = (
  weightKg: number | null,
  preferences: UserPreferences
): string => {
  if (!weightKg) return 'N/A';
  
  const weight = convertWeight(weightKg, 'kg', preferences.weight_unit);
  const unit = preferences.weight_unit;
  
  return `${weight.toFixed(1)} ${unit}`;
};

// Format distance with user preferences
export const formatDistanceDisplay = (
  distanceMeters: number | null,
  preferences: UserPreferences
): string => {
  if (!distanceMeters) return 'N/A';
  
  const distanceKm = distanceMeters / 1000;
  const distance = convertDistance(distanceKm, 'km', preferences.distance_unit);
  const unit = preferences.distance_unit;
  
  if (preferences.distance_unit === 'miles') {
    return `${distance.toFixed(2)} ${unit}`;
  }
  return `${distance.toFixed(1)} ${unit}`;
};

// Format time with user preferences
export const formatTimeDisplay = (
  timeSeconds: number | null,
  preferences: UserPreferences
): string => {
  if (!timeSeconds) return 'N/A';
  
  if (preferences.time_format === 'decimal_minutes') {
    return `${(timeSeconds / 60).toFixed(1)} min`;
  }
  
  const hours = Math.floor(timeSeconds / 3600);
  const minutes = Math.floor((timeSeconds % 3600) / 60);
  const seconds = timeSeconds % 60;
  
  if (preferences.time_format === 'hh:mm:ss' && hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Format pace with user preferences
export const formatPaceDisplay = (
  paceSecondsPerKm: number | null,
  preferences: UserPreferences
): string => {
  if (!paceSecondsPerKm) return 'N/A';
  
  switch (preferences.pace_format) {
    case 'min_per_mile': {
      const paceSecondsPerMile = paceSecondsPerKm * 1.60934;
      const minutes = Math.floor(paceSecondsPerMile / 60);
      const seconds = Math.floor(paceSecondsPerMile % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}/mi`;
    }
    case 'km_per_hour': {
      const kmPerHour = 3600 / paceSecondsPerKm;
      return `${kmPerHour.toFixed(1)} km/h`;
    }
    case 'miles_per_hour': {
      const kmPerHour = 3600 / paceSecondsPerKm;
      const milesPerHour = convertDistance(kmPerHour, 'km', 'miles');
      return `${milesPerHour.toFixed(1)} mph`;
    }
    case 'min_per_km':
    default: {
      const minutes = Math.floor(paceSecondsPerKm / 60);
      const seconds = Math.floor(paceSecondsPerKm % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
    }
  }
};

// Format temperature with user preferences
export const formatTemperatureDisplay = (
  tempCelsius: number | null,
  preferences: UserPreferences
): string => {
  if (!tempCelsius) return 'N/A';
  
  const temp = convertTemperature(tempCelsius, 'celsius', preferences.temperature_unit);
  const unit = preferences.temperature_unit === 'celsius' ? '°C' : '°F';
  
  return `${temp.toFixed(0)}${unit}`;
};

// Format percentage values
export const formatPercentageDisplay = (
  percentage: number | null,
  decimals: number = 1
): string => {
  if (!percentage && percentage !== 0) return 'N/A';
  return `${percentage.toFixed(decimals)}%`;
};

// Calculate weight change with proper formatting
export const formatWeightChangeDisplay = (
  currentKg: number,
  previousKg: number,
  preferences: UserPreferences
): {
  value: string;
  isPositive: boolean;
  isNegative: boolean;
} => {
  const changeKg = currentKg - previousKg;
  const change = convertWeight(Math.abs(changeKg), 'kg', preferences.weight_unit);
  const unit = preferences.weight_unit;
  
  return {
    value: `${changeKg >= 0 ? '+' : '-'}${change.toFixed(1)} ${unit}`,
    isPositive: changeKg > 0,
    isNegative: changeKg < 0
  };
};

// Calculate time improvement with proper formatting
export const formatTimeImprovementDisplay = (
  currentSeconds: number,
  previousSeconds: number,
  preferences: UserPreferences
): {
  value: string;
  isImprovement: boolean;
} => {
  const improvementSeconds = previousSeconds - currentSeconds;
  const isImprovement = improvementSeconds > 0;
  
  const timeFormatted = formatTimeDisplay(Math.abs(improvementSeconds), preferences);
  
  return {
    value: timeFormatted,
    isImprovement
  };
};